import React, { useEffect, useMemo, useRef } from 'react';
import { loadGsap, EASE } from './motion';

interface Props {
    /** Los oficios que se van relevando. El primero es el que se ve quieto. */
    palabras: string[];
}

/** Lo que tarda una palabra en llegar, o en irse. */
const MORFO = 0.62;
/** Lo que se queda quieta antes de empezar a irse. */
const QUIETA = 1.7;

/**
 * Una palabra que se convierte en la siguiente.
 *
 * Adaptado del «Text Morph» de Originkit, con tres cambios que no son de
 * estilo:
 *
 * 1. FUERA EL FILTRO GOOEY. El original mete un `feColorMatrix` de SVG sobre
 *    el texto para que las letras se peguen como gotas. Se ve bien y cuesta
 *    carísimo: un filtro SVG sobre contenido que se anima obliga al navegador
 *    a rasterizar de nuevo en cada cuadro, y encima corre para siempre. Esta
 *    página venía de 19 tirones de scroll en un teléfono y está en 1; no se
 *    tira eso por un efecto de gotas. Quedan el desenfoque y la escala, que
 *    son el gesto de verdad.
 *
 * 2. SOLO CORRE MIENTRAS SE VE. El original es una animación CSS infinita: en
 *    cuanto está en el DOM, gira aunque esté a tres pantallas de distancia.
 *    Aquí un ScrollTrigger la para al salir del encuadre.
 *
 * 3. NO MUEVE LA MAQUETACIÓN. La palabra más larga reserva el ancho, así que
 *    el relevo es una disolución EN EL SITIO: ni un píxel se desplaza. Por eso
 *    corre también con `prefers-reduced-motion` — la regla de la casa apaga el
 *    movimiento que arrastra la vista, no un fundido quieto. Sin JavaScript se
 *    lee la primera palabra fija; el CSS ya la deja visible por su cuenta.
 *
 * Sobre lectores de pantalla: las palabras que se relevan van ocultas al árbol
 * de accesibilidad y en su lugar se lee la enumeración entera. «Una IA que
 * sabe de techos, pintura, concreto, jardinería y remodelación» dice lo que la
 * animación quiere decir; una palabra suelta al azar, no.
 */
const OficioQueCambia: React.FC<Props> = ({ palabras }) => {
    const caja = useRef<HTMLSpanElement>(null);

    /* Se depende del CONTENIDO de la lista, no del arreglo que llega.
     *
     * Esto se rompió de verdad y el síntoma era desconcertante: la palabra se
     * quedaba clavada en la primera y solo empezaba a relevarse si dejabas de
     * hacer scroll. La causa: `Producto` se vuelve a pintar en cada tic del
     * scroll —sus ScrollTriggers llaman a `setVista`— y desde que las palabras
     * se traducen, llegan de un `.map()`, o sea un arreglo NUEVO en cada
     * render. Con `[palabras]` de dependencia, cada movimiento del dedo
     * desmontaba la línea de tiempo de GSAP y la volvía a crear desde la
     * primera palabra. Quieto, nadie la reiniciaba y por fin avanzaba.
     *
     * Comparando el contenido, da igual cuántos arreglos nuevos lleguen: si
     * dicen lo mismo, la animación sigue su curso. Y si cambian de verdad
     * —al cambiar de idioma— se rehace, que es justo lo que debe pasar.
     *
     * Se serializa con `JSON.stringify` y no con un `join`: cualquier separador
     * que se elija puede aparecer dentro de una palabra, y entonces ['a','b'] y
     * ['a,b'] darían la misma firma y una de las dos listas no se aplicaría.
     * `JSON.stringify` escapa lo que haga falta y no colisiona nunca. Son cinco
     * palabras cortas una vez por render: el coste no se mide. */
    const firma = JSON.stringify(palabras);

    /* La palabra más larga reserva el ancho. Sin esto el titular cambia de
       ancho cinco veces por vuelta y el subrayado del paso baila con él. */
    const masLarga = useMemo(
        () => palabras.reduce((a, p) => (p.length > a.length ? p : a), ''),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [firma],
    );

    const enumeracion = useMemo(
        () =>
            palabras.length > 1
                ? `${palabras.slice(0, -1).join(', ')} y ${palabras[palabras.length - 1]}`
                : palabras[0],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [firma],
    );

    useEffect(() => {
        const el = caja.current;
        if (!el) return;
        /* Sin comprobar `prefers-reduced-motion`, y es deliberado.
           La regla de la casa apaga el movimiento AUTÓNOMO: lo que se desplaza
           por la pantalla y arrastra la vista. Esto no se desplaza — una
           palabra se disuelve encima de otra, en su sitio, sin mover un solo
           píxel de la maquetación (el ancho lo reserva la palabra más larga).
           Es la misma categoría que un fundido entre dos paneles, que esta
           página ya hace en todos los niveles de movimiento.

           Si algún día hay que apagarlo de verdad, el sitio es aquí y basta
           con devolver la comprobación. */

        let vivo = true;
        let limpiar: (() => void) | undefined;

        loadGsap().then(({ gsap, ScrollTrigger }) => {
            if (!vivo || !caja.current) return;

            const ctx = gsap.context(() => {
                const ps = gsap.utils.toArray<HTMLElement>('[data-oficio]', el);
                if (!ps.length) return;

                const RANURA = MORFO + QUIETA;
                const escondida = { autoAlpha: 0, filter: 'blur(0.26em)' };

                /* Estado de espera: la primera palabra SE VE.
                   Esto es un arreglo, no un adorno. La línea de tiempo arranca
                   pausada —solo corre cuando el paso está en pantalla— y GSAP
                   escribe los valores de partida de un `fromTo` en el momento de
                   crearlo. Resultado: las cinco palabras quedaban en opacidad 0
                   desde que montaba el componente, y el titular enseñaba un
                   HUECO en vez de «techos» hasta que bajabas hasta él.
                   Por eso los `fromTo` van con `immediateRender: false`: que no
                   toquen nada hasta que la animación empiece de verdad. */
                gsap.set(ps, { ...escondida, scale: 0.88 });
                gsap.set(ps[0], { autoAlpha: 1, filter: 'blur(0em)', scale: 1 });

                const tl = gsap.timeline({ repeat: -1, paused: true });

                ps.forEach((p, i) => {
                    tl.fromTo(
                        p,
                        { ...escondida, scale: 0.88 },
                        {
                            autoAlpha: 1,
                            filter: 'blur(0em)',
                            scale: 1,
                            duration: MORFO,
                            ease: EASE.out,
                            immediateRender: false,
                        },
                        i * RANURA,
                    ).to(
                        p,
                        { ...escondida, scale: 1.1, duration: MORFO, ease: 'power2.in' },
                        /* La salida termina justo donde empieza la entrada de la
                           siguiente, y la de la última cae exactamente al final
                           de la vuelta. Así el bucle no tiene costura. */
                        (i + 1) * RANURA - MORFO,
                    );
                });

                const st = ScrollTrigger.create({
                    trigger: el,
                    start: 'top bottom',
                    end: 'bottom top',
                    onToggle: (self) => (self.isActive ? tl.play() : tl.pause()),
                });
                // `onToggle` no dispara si al crearse ya estaba dentro del
                // encuadre: sin esto, entrar a la página con el fold a la vista
                // dejaba el titular con un hueco donde va la palabra.
                if (st.isActive) tl.play();
            }, el);

            limpiar = () => ctx.revert();
        });

        return () => {
            vivo = false;
            limpiar?.();
        };
        // `firma` y no `palabras`: ver el comentario largo de arriba. Con el
        // arreglo, cada tic del scroll rehacía la animación desde cero.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [firma]);

    return (
        <span ref={caja} className="rp-oficio">
            {/* Reserva el ancho y, de paso, es lo único que lee un lector de
                pantalla de aquí dentro. */}
            <span className="rp-oficio__medida">{masLarga}</span>
            <span className="rp-oficio__lectura">{enumeracion}</span>
            {palabras.map((p, i) => (
                <span
                    key={p}
                    data-oficio
                    /* La primera es la que se ve si GSAP nunca llega. */
                    data-primera={i === 0 ? '' : undefined}
                    aria-hidden="true"
                    className="rp-oficio__palabra"
                >
                    {p}
                </span>
            ))}
        </span>
    );
};

export default OficioQueCambia;
